// src/data/carrierSender.js - RULING 305.13 (by delegation, Record 654): who sends the claim to the carrier. Bob, 305.8
// (2026-10-11), verbatim: "otherwise the General Chairperson as the 2nd default and/or whoever the General Chairperson
// swlects and assigns to this or any other particular case, in writing." Read with 305.5 item 1: the member when he
// represents himself; otherwise the Local Chairperson, the first default; otherwise the General Chairperson.
//
// THE WRITTEN ASSIGNMENT is claim.carrierSender - named, dated, attributed to the General Chairperson (or the
// administrator), on the claim (consistent with ruling 150: per matter, a named person, recorded on the matter). Only she
// writes it (firestore.rules: lcEdit refuses the field; gcEdit/adminEdit check its shape, her uid, the server clock). The
// person she names must hold an office (a Local or General Chairperson of her committee's seats); the rules let him open
// that claim and record its service on the carrier, and nothing else. PURE.
const s = (v) => (v == null ? '' : String(v)).trim();

export const SENDER_KEYS = ['uid', 'name', 'role', 'assignedByUid', 'assignedByName', 'assignedAt', 'note'];

export function mayAssignSender(user, claim) {
  if (!user || !claim || s(claim.status) === 'draft') return false;
  if (user.isAdmin === true || s(user.role) === 'admin') return true;
  const committee = s(claim.committee || claim.generalCommittee);
  return s(user.role) === 'general_chair' && !!committee && s(user.committee) === committee;
}

// The line every party reads: who sends this claim to the carrier, and on what footing.
export function whoSends(claim) {
  const c = claim || {};
  const a = c.carrierSender;
  if (a && s(a.uid)) {
    return { kind: 'assigned', uid: s(a.uid), line: 'Sends it to the carrier: ' + (s(a.name) || 'the person named') + ' - assigned in writing by '
      + (s(a.assignedByName) || 'the General Chairperson') + '.' };
  }
  // Carriage (rulings 135/144/294) is who represents the claim: a filed claim the claimant still carries is one she carries
  // herself - she represents herself, and she sends it.
  if (s(c.carriedBy) === 'claimant' && s(c.status) !== 'draft') {
    return { kind: 'member', uid: s(c.claimantUid), line: 'Sends it to the carrier: the member, who carries the claim herself.' };
  }
  return { kind: 'local_chair', uid: '', line: 'Sends it to the carrier: the Local Chairperson (the first default); the General '
    + 'Chairperson if he does not (the second default); or whoever the General Chairperson assigns in writing.' };
}

// person: { uid, name, role } from the committee's seats (or the GC herself). serverTime: serverTimestamp().
export function assignPatch(claim, user, person, note, serverTime) {
  const errors = [];
  if (!mayAssignSender(user, claim)) errors.push('Only the General Chairperson of this claim\'s committee assigns this.');
  const p = person || {};
  if (!s(p.uid)) errors.push('Name the person.');
  if (!['local_chair', 'general_chair'].includes(s(p.role))) errors.push('Assign a Local or General Chairperson.');
  if (s(note).length > 500) errors.push('Keep the note under 500 characters.');
  if (errors.length) return { ok: false, errors, patch: null };
  return { ok: true, errors: [], patch: { carrierSender: {
    uid: s(p.uid), name: s(p.name) || s(p.uid), role: s(p.role),
    assignedByUid: user.uid, assignedByName: s(user.name || user.email), assignedAt: serverTime, note: s(note),
  } } };
}
export function withdrawPatch(claim, user) {
  if (!mayAssignSender(user, claim)) return { ok: false, errors: ['Only the General Chairperson of this claim\'s committee withdraws it.'], patch: null };
  if (!claim || !claim.carrierSender) return { ok: false, errors: ['There is no written assignment to withdraw.'], patch: null };
  return { ok: true, errors: [], patch: { carrierSender: null } };
}
// The named sender who is NOT otherwise an officer of this claim records service through his own narrow arm (presentment
// fields only, as the claimant does) - the page must send him down that path, not the officer's.
export function isAssignedSenderOnly(claim, user) {
  const a = claim && claim.carrierSender;
  if (!a || !user || s(a.uid) !== s(user.uid)) return false;
  if (user.isAdmin === true) return false;
  const committee = s(claim.committee || claim.generalCommittee);
  if (s(user.role) === 'general_chair' && s(user.committee) === committee) return false;
  if (s(user.role) === 'local_chair' && Array.isArray(user.scopes) && user.scopes.indexOf(claim.scopeKey) !== -1) return false;
  return true;
}
